import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../utils/api';

// Use dynamic URL getter to ensure correct host in production
const getAPI = () => `${getBackendUrl()}/api`;

const TeamsContext = createContext(null);

// No default teams — new dealers start with a blank slate
const DEFAULT_TEAMS = [];

export const TeamsProvider = ({ children }) => {
  const [teams, setTeams] = useState([]);
  const [activeTeams, setActiveTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch teams from API
  const fetchTeams = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        // Use default teams if not logged in
        setTeams(DEFAULT_TEAMS);
        setActiveTeams(DEFAULT_TEAMS);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${getAPI()}/sales-teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fetchedTeams = response.data || [];
      
      // If no teams exist, use defaults
      if (fetchedTeams.length === 0) {
        setTeams(DEFAULT_TEAMS);
        setActiveTeams(DEFAULT_TEAMS);
      } else {
        setTeams(fetchedTeams);
        setActiveTeams(fetchedTeams.filter(t => t.is_active !== false));
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching teams:', err);
      // Fall back to default teams on error
      setTeams(DEFAULT_TEAMS);
      setActiveTeams(DEFAULT_TEAMS);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeams();
    
    // Re-fetch teams when token changes (after login)
    const handleStorageChange = (e) => {
      if (e.key === 'token' && e.newValue) {
        console.log('Token changed, refetching teams...');
        fetchTeams();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom auth event
    const handleAuthChange = () => {
      console.log('Auth event, refetching teams...');
      fetchTeams();
    };
    window.addEventListener('authStateChanged', handleAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleAuthChange);
    };
  }, [fetchTeams]);

  // Get team by name (for backward compatibility with hardcoded names)
  const getTeamByName = useCallback((name) => {
    return teams.find(t => t.name === name || t.code === name) || null;
  }, [teams]);

  // Get team color by name
  const getTeamColor = useCallback((name) => {
    const team = getTeamByName(name);
    return team?.color || '#6b7280'; // Default gray if not found
  }, [getTeamByName]);

  // Get team display name
  const getTeamDisplayName = useCallback((name) => {
    const team = getTeamByName(name);
    return team?.name || name;
  }, [getTeamByName]);

  // Check if team is active/visible
  const isTeamActive = useCallback((name) => {
    const team = getTeamByName(name);
    return team ? team.is_active !== false : true;
  }, [getTeamByName]);

  // Update a team
  const updateTeam = useCallback(async (teamId, updateData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${getAPI()}/sales-teams/${teamId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh teams after update
      await fetchTeams();
      return response.data;
    } catch (err) {
      console.error('Error updating team:', err);
      throw err;
    }
  }, [fetchTeams]);

  // Toggle team visibility
  const toggleTeamVisibility = useCallback(async (teamId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${getAPI()}/sales-teams/${teamId}/visibility`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh teams after toggle
      await fetchTeams();
      return response.data;
    } catch (err) {
      console.error('Error toggling team visibility:', err);
      throw err;
    }
  }, [fetchTeams]);

  // Delete (hide) a team
  const deleteTeam = useCallback(async (teamId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${getAPI()}/sales-teams/${teamId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh teams after delete
      await fetchTeams();
      return response.data;
    } catch (err) {
      console.error('Error deleting team:', err);
      throw err;
    }
  }, [fetchTeams]);

  // Create a new team
  const createTeam = useCallback(async (teamData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getAPI()}/sales-teams`,
        teamData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh teams after create
      await fetchTeams();
      return response.data;
    } catch (err) {
      console.error('Error creating team:', err);
      throw err;
    }
  }, [fetchTeams]);

  const value = {
    teams,           // All teams (including hidden)
    activeTeams,     // Only visible teams
    loading,
    error,
    fetchTeams,
    getTeamByName,
    getTeamColor,
    getTeamDisplayName,
    isTeamActive,
    updateTeam,
    toggleTeamVisibility,
    deleteTeam,
    createTeam
  };

  return (
    <TeamsContext.Provider value={value}>
      {children}
    </TeamsContext.Provider>
  );
};

export const useTeams = () => {
  const context = useContext(TeamsContext);
  if (!context) {
    throw new Error('useTeams must be used within a TeamsProvider');
  }
  return context;
};

export default TeamsContext;
